import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from './UserContext';
import { COLLECTION_USERS, orgMemberPath, orgPath } from '../lib/firestorePaths';
import type { Membership, Organization } from '../types';

type OrgContextValue = {
  org: Organization | null;
  membership: Membership | null;
  loading: boolean;
};

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgIdResolved, setOrgIdResolved] = useState(false);
  const [org, setOrg] = useState<Organization | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [orgLoaded, setOrgLoaded] = useState(false);

  // Resolve the user's orgId mirror on users/{uid}.
  useEffect(() => {
    if (!user) {
      setOrgId(null);
      setOrgIdResolved(false);
      return;
    }
    setOrgIdResolved(false);
    const unsubscribe = onSnapshot(
      doc(db, COLLECTION_USERS, user.uid),
      (snap) => {
        const nextOrgId = snap.exists() ? (snap.data().orgId as string | undefined) ?? null : null;
        setOrgId(nextOrgId);
        setOrgIdResolved(true);
      },
      (error) => {
        console.error('Failed to resolve orgId:', error);
        setOrgId(null);
        setOrgIdResolved(true);
      }
    );
    return unsubscribe;
  }, [user]);

  // Subscribe org + own membership once orgId is known.
  useEffect(() => {
    if (!user || !orgId) {
      setOrg(null);
      setMembership(null);
      setOrgLoaded(false);
      return;
    }
    setOrgLoaded(false);
    let orgReady = false;
    let memberReady = false;
    const markReady = () => {
      if (orgReady && memberReady) setOrgLoaded(true);
    };

    const unsubOrg = onSnapshot(
      doc(db, orgPath(orgId)),
      (snap) => {
        setOrg(snap.exists() ? ({ ...(snap.data() as Organization), id: snap.id }) : null);
        orgReady = true;
        markReady();
      },
      (error) => {
        console.error('Failed to load organization:', error);
        setOrg(null);
        orgReady = true;
        markReady();
      }
    );
    const unsubMember = onSnapshot(
      doc(db, orgMemberPath(orgId, user.uid)),
      (snap) => {
        setMembership(snap.exists() ? ({ ...(snap.data() as Membership), uid: snap.id }) : null);
        memberReady = true;
        markReady();
      },
      (error) => {
        console.error('Failed to load membership:', error);
        setMembership(null);
        memberReady = true;
        markReady();
      }
    );
    return () => {
      unsubOrg();
      unsubMember();
    };
  }, [user, orgId]);

  const loading = Boolean(user) && (!orgIdResolved || (orgId !== null && !orgLoaded));

  const value: OrgContextValue = {
    org: orgId ? org : null,
    membership: orgId ? membership : null,
    loading,
  };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const context = useContext(OrgContext);
  if (!context) throw new Error('useOrg must be used within an OrgProvider');
  return context;
}
